import { Link } from "react-router-dom";
import type { ContentItem } from "@/mocks/content";
import { formatDate, typeLabel, typePath } from "@/lib/content";
import HighlightText from "@/components/feature/HighlightText";

interface ContentCardProps {
  item: ContentItem;
  layout?: "vertical" | "horizontal";
  highlight?: string;
}

export default function ContentCard({ item, layout = "vertical", highlight }: ContentCardProps) {
  const isWide = layout === "horizontal";

  return (
    <Link
      to={`/detail/${item.id}`}
      className={`group flex overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-colors hover:border-primary-200 ${
        isWide ? "flex-col sm:flex-row" : "flex-col"
      }`}
    >
      <div
        className={`relative shrink-0 overflow-hidden bg-background-200 ${
          isWide ? "h-44 w-full sm:h-auto sm:w-[220px]" : "aspect-[8/5] w-full"
        }`}
      >
        <img
          src={item.cover}
          alt={`${item.title} - 剧集封面`}
          title={`${item.title} · 嫂子的诱惑剧集封面`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="absolute left-3 top-3 rounded-full bg-primary-950/75 px-2.5 py-1 text-[11px] font-medium text-background-50 backdrop-blur-sm">
          {typeLabel[item.type]}
        </span>
      </div>

      <div className={`flex flex-1 flex-col ${isWide ? "p-4 md:p-5" : "p-4"}`}>
        <h3
          className={`font-heading font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 ${
            isWide ? "text-base md:text-lg" : "line-clamp-2 text-[15px] md:text-base"
          }`}
        >
          {highlight ? <HighlightText text={item.title} keyword={highlight} /> : item.title}
        </h3>
        <p className="mt-1.5 text-xs text-foreground-500">
          {item.subtitle} · {formatDate(item.date)}
        </p>
        <p
          className={`mt-2.5 text-sm leading-relaxed text-foreground-600 ${
            isWide ? "line-clamp-3" : "line-clamp-2"
          }`}
        >
          {highlight ? <HighlightText text={item.excerpt} keyword={highlight} /> : item.excerpt}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {item.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between pt-3">
          <span className="text-[11px] text-foreground-400">阅读全文</span>
          <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
            查看
            <i className="ri-arrow-right-up-line text-sm transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ContentListLink({ item }: { item: ContentItem }) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex items-start gap-3 border-b border-background-200/70 py-3.5 last:border-none"
    >
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-[11px] font-semibold text-accent-800">
        {typeLabel[item.type].slice(0, 1)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
          {item.title}
        </span>
        <span className="mt-1 block text-[11px] text-foreground-400">
          {formatDate(item.date)} · {item.subtitle}
        </span>
      </span>
      <i className="ri-arrow-right-s-line mt-1 shrink-0 text-base text-foreground-300 transition-colors group-hover:text-primary-500" />
    </Link>
  );
}

export function CategoryBadge({ type }: { type: ContentItem["type"] }) {
  return (
    <Link
      to={typePath[type]}
      className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-200 hover:text-primary-700"
    >
      <i className="ri-folder-line text-sm" />
      {typeLabel[type]}
    </Link>
  );
}